import gsap from "gsap";
import { calculateCardPosition } from "./calculate-card-position.js";

export function createInfocardController({
  onPrevious,
  onNext,
  onBack,
  targetDocument = document,
  targetWindow = window,
}) {
  const cards = Array.from(
    targetDocument.querySelectorAll(".infocard__item"),
  );
  const root = targetDocument.querySelector(".infocards");
  const navigation = targetDocument.querySelector(".infocards__navigation");
  const backButton = targetDocument.querySelector(".infocards__back");
  const previousButton = targetDocument.querySelector(".infocards__previous");
  const nextButton = targetDocument.querySelector(".infocards__next");
  const currentTitle = targetDocument.querySelector(".infocards__current");
  let active = null;

  previousButton?.addEventListener("click", onPrevious);
  nextButton?.addEventListener("click", onNext);
  backButton?.addEventListener("click", onBack);

  function hideAll() {
    cards.forEach((card) => gsap.set(card, { display: "none" }));
  }

  return {
    getAnimationTargets() {
      return [...cards, navigation].filter(Boolean);
    },
    hideAll,
    setNavigation({ previous, current, next }) {
      if (currentTitle) currentTitle.textContent = current.label;
      previousButton?.setAttribute(
        "aria-label",
        `Vorheriger Hotspot: ${previous.label}`,
      );
      nextButton?.setAttribute(
        "aria-label",
        `Nächster Hotspot: ${next.label}`,
      );
    },
    appendShow(timeline, cardName, hotspot) {
      const card = cards.find((item) => item.dataset.card === cardName);
      if (!card) return;

      cards.forEach((item) => {
        if (item !== card) gsap.set(item, { display: "none" });
      });

      const nextActive = { card, hotspot, visible: false };
      active = nextActive;
      const animationStart = Math.max(0, timeline.duration() - 0.6);

      timeline.fromTo(
        card,
        {
          display: "block",
          autoAlpha: 0,
          y: 32,
          scale: 0.92,
        },
        {
          duration: 0.8,
          autoAlpha: 1,
          y: 0,
          scale: 1,
          ease: "power3.out",
          clearProps: "opacity,visibility,transform",
          onStart: () => {
            if (active === nextActive) active.visible = true;
          },
        },
        animationStart,
      );

      const navigationIsVisible =
        navigation &&
        targetWindow.getComputedStyle(navigation).display !== "none";

      if (navigation && !navigationIsVisible) {
        timeline.fromTo(
          navigation,
          { display: "inline-flex", autoAlpha: 0, x: -16 },
          {
            duration: 0.6,
            autoAlpha: 1,
            x: 0,
            ease: "power3.out",
            clearProps: "opacity,visibility,transform",
          },
          animationStart,
        );
      }
    },
    takeActiveCard() {
      const card = active?.card ?? null;
      active = null;
      return card;
    },
    appendHide(timeline, card) {
      timeline.to(
        card,
        {
          duration: 0.4,
          autoAlpha: 0,
          y: 20,
          scale: 0.96,
          ease: "power2.in",
          onComplete: () => gsap.set(card, { display: "none" }),
        },
        0,
      );

      if (navigation) {
        timeline.to(
          navigation,
          {
            duration: 0.3,
            autoAlpha: 0,
            x: -16,
            ease: "power2.in",
            onComplete: () => gsap.set(navigation, { display: "none" }),
          },
          0,
        );
      }
    },
    positionActive(hotspot, x, y, viewport) {
      if (!active?.visible || active.hotspot !== hotspot) return;

      const { card } = active;
      const navigationBounds = navigation?.getBoundingClientRect();
      const navigationGap = viewport.width <= 720 ? 12 : 16;
      const fallbackTopInset = Number.parseFloat(
        root
          ? targetWindow
              .getComputedStyle(root)
              .getPropertyValue("--infocard-navigation-clearance")
          : "",
      );
      const topInset = navigationBounds?.height
        ? navigationBounds.bottom + navigationGap
        : Number.isFinite(fallbackTopInset)
          ? fallbackTopInset
          : 16;
      const { left, top } = calculateCardPosition({
        x,
        y,
        hotspotRadius: hotspot.offsetWidth / 2,
        cardWidth: card.offsetWidth,
        cardHeight: card.offsetHeight,
        viewport,
        topInset,
      });

      card.style.setProperty("--card-left", `${left}px`);
      card.style.setProperty("--card-top", `${top}px`);
    },
    destroy() {
      previousButton?.removeEventListener("click", onPrevious);
      nextButton?.removeEventListener("click", onNext);
      backButton?.removeEventListener("click", onBack);
      active = null;
    },
  };
}
